import { ColorVariant } from 'styling/GlobalStyles'
import { SmallText } from 'styling/TypographyV2'
import { tokens } from 'styling/tokens'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import {
  IPreassignDataResponse,
  PreassignActionType,
} from 'features/operationalManagement/operationalManagement.types'
import {
  Arrow,
  KpiRow,
  Panel,
  PanelSub,
  PanelTableHeader,
  PanelTableRow,
  PanelTitle,
  ReasonText,
} from '../BulkAssign.styled'

interface Props {
  loading: boolean
  data: IPreassignDataResponse | null
}

const fullName = (u: { firstName: string; lastName: string }) =>
  `${u.firstName} ${u.lastName}`.trim()

export const PreFlightStep = ({ loading, data }: Props) => {
  if (loading) {
    return <SmallText colorVariant={ColorVariant.SECONDARY}>Running pre-flight…</SmallText>
  }
  if (!data) {
    return (
      <SmallText colorVariant={ColorVariant.SECONDARY}>
        No preview data available.
      </SmallText>
    )
  }

  const { summary, conflicts, reassignments, readyUsers } = data
  const empCols = '1.4fr 1fr 1.2fr 1.2fr'
  const empColsMid = '1.4fr 1fr 2fr'
  const empColsReady = '1.4fr 1fr 1.2fr 1.2fr'

  return (
    <>
      <KpiRow>
        <MetricCard
          label="Selected"
          value={String(summary.selectedUserCount)}
          accentColor={tokens.status.info.fg}
        />
        <MetricCard
          label="Eligible"
          value={String(summary.readyUserCount)}
          accentColor={tokens.status.success.fg}
        />
        <MetricCard
          label="Conflict"
          value={String(summary.conflictUserCount)}
          accentColor={tokens.status.danger.fg}
        />
        <MetricCard
          label="Mid-shift"
          value={String(summary.reassignmentUserCount)}
          accentColor={tokens.accent.base}
        />
      </KpiRow>

      {conflicts.length > 0 && (
        <Panel accentColor={tokens.status.danger.fg}>
          <PanelTitle>Conflicts — Will Be Skipped</PanelTitle>
          <PanelSub>Subtitle goes here</PanelSub>
          <PanelTableHeader cols={empCols}>
            <div>EMPLOYEE</div>
            <div>ID</div>
            <div style={{ gridColumn: '3 / span 2' }}>REASON</div>
          </PanelTableHeader>
          {conflicts.map((c, i) => (
            <PanelTableRow cols={empCols} key={`${c.user.id}-${i}`}>
              <div>{fullName(c.user)}</div>
              <div style={{ color: tokens.text.secondary }}>EMP-{String(c.user.id).padStart(3, '0')}</div>
              <ReasonText style={{ gridColumn: '3 / span 2' }}>{c.reason}</ReasonText>
            </PanelTableRow>
          ))}
        </Panel>
      )}

      {reassignments.length > 0 && (
        <Panel accentColor={tokens.accent.base}>
          <PanelTitle>Mid-Shift Reassignments</PanelTitle>
          <PanelSub>
            These employees are currently active on shift. Availability effects begin next shift
            start.
          </PanelSub>
          <PanelTableHeader cols={empColsMid}>
            <div>EMPLOYEE</div>
            <div>ID</div>
            <div>TRANSITION</div>
          </PanelTableHeader>
          {reassignments.map((r, i) => (
            <PanelTableRow cols={empColsMid} key={`${r.user.id}-${i}`}>
              <div>{fullName(r.user)}</div>
              <div style={{ color: tokens.text.secondary }}>EMP-{String(r.user.id).padStart(3, '0')}</div>
              <div>
                <span style={{ color: tokens.accent.base }}>{r.fromTeamName}</span>
                <Arrow>→</Arrow>
                <span style={{ color: tokens.status.success.fg }}>{r.toTeamName}</span>
              </div>
            </PanelTableRow>
          ))}
        </Panel>
      )}

      {readyUsers.length > 0 && (
        <Panel accentColor={tokens.status.success.fg}>
          <PanelTitle>Ready to Assign</PanelTitle>
          <PanelSub>Subtitle goes here</PanelSub>
          <PanelTableHeader cols={empColsReady}>
            <div>EMPLOYEE</div>
            <div>ID</div>
            <div>ROLE</div>
            <div>TYPE</div>
          </PanelTableHeader>
          {readyUsers.map((r, i) => (
            <PanelTableRow cols={empColsReady} key={`${r.user.id}-${i}`}>
              <div>{fullName(r.user)}</div>
              <div style={{ color: tokens.text.secondary }}>EMP-{String(r.user.id).padStart(3, '0')}</div>
              <div>{r.user.currentRole?.name ?? '—'}</div>
              <div>
                {r.actionType === PreassignActionType.Assign ? 'Assign' : 'Reassign'}
              </div>
            </PanelTableRow>
          ))}
        </Panel>
      )}
    </>
  )
}
